import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Calendar, MapPin } from "lucide-react";

function EventCard({ e, past = false }) {
    return (
        <div
            data-testid={`event-${e.id}`}
            className={`bg-card border border-border hover:border-primary/40 transition-all ${past ? "opacity-90" : ""}`}
        >
            <div className="aspect-[16/9] overflow-hidden bg-muted">
                <img src={e.cover_image} alt={e.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
                <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-secondary" />{new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-secondary" />{e.venue}</span>
                </div>
                <h3 className="font-display text-2xl leading-tight">{e.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{e.description}</p>
            </div>
        </div>
    );
}

export default function Events() {
    const q = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
    const events = q.data || [];
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = events.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
    const past = events.filter((e) => e.date < today);

    return (
        <div data-testid="events-page">
            <PageHeader
                eyebrow="Events"
                title={<>Where you'll find us <span className="text-secondary italic font-light">next</span>.</>}
                sub="Upcoming drives, workshops and ceremonies — plus a record of recent ones."
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16">
                <div className="font-eyebrow text-xs text-secondary mb-3">Upcoming</div>
                <h2 className="font-display text-2xl lg:text-4xl tracking-tight mb-8">Join us on the ground</h2>
                {upcoming.length === 0 ? (
                    <p className="text-muted-foreground">No upcoming events scheduled. Check back soon.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {upcoming.map((e) => <EventCard key={e.id} e={e} />)}
                    </div>
                )}
            </section>

            <section className="mx-auto max-w-7xl px-5 lg:px-10 pb-20">
                <div className="font-eyebrow text-xs text-secondary mb-3">Past</div>
                <h2 className="font-display text-2xl lg:text-4xl tracking-tight mb-8">From our archive</h2>
                {past.length === 0 ? (
                    <p className="text-muted-foreground">No past events to show yet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {past.map((e) => <EventCard key={e.id} e={e} past />)}
                    </div>
                )}
            </section>
        </div>
    );
}
